import { Link } from "react-router-dom";
import { ArrowRight, Building2, HardHat, ClipboardList, Boxes, Ship, ShieldCheck, Clock, Wrench, Cog, Truck, Sofa } from "lucide-react";
import { useT } from "@/context/LangContext";
import { SECTORS } from "@/constants/sectors";
import TikTokSection from "@/components/TikTokSection";
import FeaturedProducts from "@/components/FeaturedProducts";
import Testimonials from "@/components/Testimonials";

const HERO_IMG = "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwxfHxjb25zdHJ1Y3Rpb24lMjBjcmFuZSUyMHNreWxpbmUlMjBjaGluYXxlbnwwfHx8fDE3ODEzODc3NjB8MA&ixlib=rb-4.1.0&q=85";
const ABOUT_IMG = "https://images.unsplash.com/photo-1553877522-43269d4ea984?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwyfHxjb25zdHJ1Y3Rpb24lMjBibHVlcHJpbnQlMjBhcmNoaXRlY3R8ZW58MHx8fHwxNzgxMzg3NzYwfDA&ixlib=rb-4.1.0&q=85";
const LOGI_IMG = "https://images.unsplash.com/photo-1587293852726-70cdb56c2866?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwxfHx3YXJlaG91c2UlMjBsb2dpc3RpY3MlMjBtb2Rlcm4lMjBicmlnaHR8ZW58MHx8fHwxNzgxMzg3NzYwfDA&ixlib=rb-4.1.0&q=85";

const TRUST = ["YIWU", "GUANGZHOU", "MAERSK", "CMA CGM", "DHL", "CCIC"];

export default function Home() {
  const t = useT();

  const services = [
    { icon: HardHat, k: "construction" },
    { icon: Wrench, k: "renovation" },
    { icon: ClipboardList, k: "onestop" },
    { icon: Boxes, k: "sourcing" },
    { icon: ShieldCheck, k: "quality" },
    { icon: Ship, k: "logistics" },
  ];

  const sectors = SECTORS;

  const whys = [1, 2, 3, 4];

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[88vh] flex items-end overflow-hidden">
        <img src={HERO_IMG} alt="construction" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative max-w-7xl w-full mx-auto px-6 lg:px-10 pb-20 pt-28 text-white grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8 animate-fade-up">
            <p className="kicker !text-[#FF6B00]">{t("home.kicker")}</p>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter mt-6 max-w-4xl">
              {t("home.title1")} <br />
              <span className="text-[var(--accent)]">{t("home.title2")}</span> {t("home.title3")}
            </h1>
            <p className="text-white/70 mt-6 max-w-2xl text-base sm:text-lg leading-relaxed">
              {t("home.subtitle")}
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/contact" className="btn-primary" data-testid="hero-cta-quote">
                {t("home.cta.quote")} <ArrowRight size={18} />
              </Link>
              <Link to="/services" className="btn-outline !text-white !border-white/40 hover:!bg-white hover:!text-[var(--ink)]" data-testid="hero-cta-services">
                {t("home.cta.services")}
              </Link>
            </div>
          </div>
          <div className="lg:col-span-4 hidden lg:flex flex-col justify-end gap-4">
            {[
              { v: "12+", l: t("home.stats.years") },
              { v: "2", l: t("home.stats.offices") },
              { v: "4", l: t("home.stats.sectors") },
            ].map((s, i) => (
              <div key={i} className="border-l-2 border-[var(--accent)] pl-4">
                <p className="font-display text-4xl font-black">{s.v}</p>
                <p className="text-white/60 text-xs uppercase tracking-[0.2em] mt-1">{s.l}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Marquee trust */}
      <section className="border-y border-black/5 py-6 overflow-hidden bg-[var(--bg-soft)]">
        <div className="marquee whitespace-nowrap">
          {[...TRUST, ...TRUST].map((tv, i) => (
            <span key={i} className="font-display font-black text-2xl tracking-tighter text-black/40">
              {tv} <span className="text-[var(--accent)]">×</span>
            </span>
          ))}
        </div>
      </section>

      {/* Featured products — high on the page for shop discovery */}
      <FeaturedProducts />

      {/* SERVICES BENTO */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 lg:gap-12 mb-12">
          <div className="md:col-span-5">
            <p className="kicker">{t("home.services.kicker")}</p>
            <h2 className="text-4xl lg:text-5xl font-display font-black mt-4">
              {t("home.services.title1")} <br />{t("home.services.title2")}
            </h2>
          </div>
          <div className="md:col-span-6 md:col-start-7 self-end">
            <p className="text-[var(--ink-soft)] text-lg leading-relaxed">{t("home.services.desc")}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {services.map((s, i) => {
            const Icon = s.icon;
            const span = i === 0 ? "md:col-span-5" : i === 1 ? "md:col-span-7" : i === 2 ? "md:col-span-7" : i === 3 ? "md:col-span-5" : i === 4 ? "md:col-span-4" : "md:col-span-8";
            return (
              <div key={i} className={`card-flat p-8 ${span}`} data-testid={`service-card-${i}`}>
                <Icon className="text-[var(--accent)]" size={28} />
                <h3 className="font-display text-2xl font-bold mt-6">{t(`svc2.${s.k}.t`)}</h3>
                <p className="text-[var(--ink-soft)] mt-3">{t(`svc2.${s.k}.d`)}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* SECTORS */}
      <section className="bg-[#0A0F1A] text-white py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-12">
            <div className="md:col-span-6">
              <p className="kicker !text-[#FF6B00]">{t("home.sectors.kicker")}</p>
              <h2 className="text-4xl lg:text-5xl font-display font-black mt-4 leading-[1.05]">
                {t("home.sectors.title")}
              </h2>
            </div>
            <div className="md:col-span-5 md:col-start-8 self-end">
              <p className="text-white/60 text-lg leading-relaxed">{t("home.sectors.desc")}</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {sectors.map((s) => {
              const Icon = s.icon;
              return (
                <Link
                  key={s.slug}
                  to={`/secteurs/${s.slug}`}
                  className="group relative block overflow-hidden rounded-lg aspect-[4/5] border border-white/10 hover:border-[var(--accent)] transition-colors"
                  data-testid={`sector-${s.slug}`}
                >
                  <img src={s.hero} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30 group-hover:opacity-50 group-hover:scale-105 transition-all duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A0F1A] via-[#0A0F1A]/70 to-transparent" />
                  <div className="absolute inset-0 p-6 flex flex-col justify-end">
                    <Icon size={22} style={{ color: s.accent }} />
                    <h3 className="font-display text-xl font-bold mt-4">{t(`sector.${s.slug}.t`)}</h3>
                    <p className="text-white/50 mt-2 text-sm">{t(`sector.${s.slug}.d`)}</p>
                    <span className="mt-4 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[var(--accent)] group-hover:gap-3 transition-all">
                      {t("home.sectors.discover")} <ArrowRight size={12} />
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* ABOUT split */}
      <section className="bg-[var(--bg-soft)] py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 order-2 lg:order-1">
            <p className="kicker">{t("home.about.kicker")}</p>
            <h2 className="text-4xl lg:text-5xl font-display font-black mt-4 leading-[1.05]">
              {t("home.about.title1")} <br />{t("home.about.title2")}
            </h2>
            <p className="text-[var(--ink-soft)] mt-6 leading-relaxed">{t("home.about.desc")}</p>
            <div className="grid grid-cols-2 gap-6 mt-10">
              <div>
                <p className="font-display text-3xl font-black text-[var(--accent)]">1</p>
                <p className="text-xs uppercase tracking-widest text-[var(--ink-soft)] mt-1">{t("home.about.stat1")}</p>
              </div>
              <div>
                <p className="font-display text-3xl font-black text-[var(--accent)]">48h</p>
                <p className="text-xs uppercase tracking-widest text-[var(--ink-soft)] mt-1">{t("home.about.stat2")}</p>
              </div>
            </div>
            <Link to="/a-propos" className="btn-outline mt-10 inline-flex" data-testid="about-cta">
              {t("home.about.more")} <ArrowRight size={16} />
            </Link>
          </div>
          <div className="lg:col-span-6 order-1 lg:order-2 relative">
            <img src={ABOUT_IMG} alt="business" className="w-full rounded-lg object-cover aspect-[4/5]" />
            <div className="absolute -bottom-6 -left-6 bg-[var(--ink)] text-white p-6 rounded-lg max-w-[260px] hidden md:block">
              <Clock size={20} className="text-[var(--accent)]" />
              <p className="text-sm mt-3 leading-snug">{t("home.about.tracking")}</p>
            </div>
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <p className="kicker">{t("why.kicker")}</p>
        <h2 className="text-4xl lg:text-5xl font-display font-black mt-4 max-w-3xl">{t("why.title")}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
          {whys.map((n) => (
            <div key={n} className="card-flat p-8" data-testid={`why-${n}`}>
              <p className="font-display text-5xl font-black text-[var(--accent)]">✓</p>
              <h3 className="font-display text-lg font-bold mt-4">{t(`why.${n}.t`)}</h3>
              <p className="text-[var(--ink-soft)] text-sm mt-2">{t(`why.${n}.d`)}</p>
            </div>
          ))}
        </div>
      </section>

      {/* LOGISTICS IMAGE BAND */}
      <section className="relative h-[60vh] overflow-hidden">
        <img src={LOGI_IMG} alt="warehouse" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/40 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 h-full flex items-center text-white">
          <div className="max-w-xl">
            <p className="kicker !text-[#FF6B00]">{t("home.logi.kicker")}</p>
            <h2 className="text-3xl lg:text-5xl font-display font-black mt-4">{t("home.logi.title")}</h2>
            <p className="text-white/70 mt-4">{t("home.logi.desc")}</p>
            <Link to="/contact" className="btn-primary mt-8 inline-flex" data-testid="logistics-cta">
              {t("home.logi.cta")} <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* TikTok videos */}
      <TikTokSection />

      {/* Client testimonials */}
      <Testimonials />

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 text-center">
        <p className="kicker">{t("home.cta.kicker")}</p>
        <h2 className="text-4xl lg:text-6xl font-display font-black mt-4 max-w-3xl mx-auto leading-[1.05]">
          {t("home.cta.title1")} <span className="text-[var(--accent)]">{t("home.cta.title2")}</span>.
        </h2>
        <div className="mt-10 flex flex-wrap justify-center gap-4">
          <Link to="/contact" className="btn-primary" data-testid="cta-contact">{t("home.cta.contact")} <ArrowRight size={16} /></Link>
          <Link to="/register" className="btn-outline" data-testid="cta-register">{t("home.cta.register")}</Link>
        </div>
      </section>
    </div>
  );
}
